import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { adminGetSite, adminUpdateSite, apiErr } from "@/lib/api";

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-charcoal/60">{label}</Label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div className="border border-charcoal/10 bg-white p-5">
    <h3 className="mb-4 font-serif text-xl text-charcoal">{title}</h3>
    <div className="grid gap-4 sm:grid-cols-2">{children}</div>
  </div>
);

function SiteForm({ site }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    slogan: site.slogan || "", sub_slogan: site.sub_slogan || "",
    whatsapp: site.whatsapp || "", whatsapp_display: site.whatsapp_display || "",
    phone: site.phone || "", phone_link: site.phone_link || "", address: site.address || "",
    instagram: site.instagram || "", instagram_handle: site.instagram_handle || "", founded: site.founded || "",
    hero_eyebrow: site.hero?.eyebrow || "", hero_title_top: site.hero?.title_top || "",
    hero_title_bottom: site.hero?.title_bottom || "", hero_subtitle: site.hero?.subtitle || "",
    wk_title: site.weekly_recommendation?.title || "", wk_text: site.weekly_recommendation?.text || "",
    working_hours: (site.working_hours || []).map((h) => ({ ...h })),
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setHour = (i, k, v) => setForm((f) => { const wh = [...f.working_hours]; wh[i] = { ...wh[i], [k]: v }; return { ...f, working_hours: wh }; });
  const addHour = () => setForm((f) => ({ ...f, working_hours: [...f.working_hours, { day: "", hours: "" }] }));
  const rmHour = (i) => setForm((f) => ({ ...f, working_hours: f.working_hours.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateSite({
        slogan: form.slogan, sub_slogan: form.sub_slogan, whatsapp: form.whatsapp, whatsapp_display: form.whatsapp_display,
        phone: form.phone, phone_link: form.phone_link, address: form.address,
        instagram: form.instagram, instagram_handle: form.instagram_handle, founded: form.founded,
        hero: { ...(site.hero || {}), eyebrow: form.hero_eyebrow, title_top: form.hero_title_top, title_bottom: form.hero_title_bottom, subtitle: form.hero_subtitle },
        weekly_recommendation: { ...(site.weekly_recommendation || {}), title: form.wk_title, text: form.wk_text },
        working_hours: form.working_hours.filter((h) => h.day || h.hours),
      });
      toast.success("Site ayarları kaydedildi");
      qc.invalidateQueries({ queryKey: ["admin", "site"] });
      qc.invalidateQueries({ queryKey: ["site"] });
    } catch (e) {
      toast.error(apiErr(e, "Kaydedilemedi"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="site-settings-form" className="space-y-4">
      <Section title="Genel">
        <Field label="Slogan"><Input data-testid="site-slogan" value={form.slogan} onChange={(e) => set("slogan", e.target.value)} /></Field>
        <Field label="Alt Slogan"><Input value={form.sub_slogan} onChange={(e) => set("sub_slogan", e.target.value)} /></Field>
        <Field label="WhatsApp Numarası (905...)"><Input data-testid="site-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
        <Field label="WhatsApp Görünen"><Input value={form.whatsapp_display} onChange={(e) => set("whatsapp_display", e.target.value)} /></Field>
        <Field label="Telefon"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="Telefon Link (+90...)"><Input value={form.phone_link} onChange={(e) => set("phone_link", e.target.value)} /></Field>
        <Field label="Instagram URL"><Input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} /></Field>
        <Field label="Instagram Kullanıcı"><Input value={form.instagram_handle} onChange={(e) => set("instagram_handle", e.target.value)} /></Field>
        <Field label="Adres" full><Textarea value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} /></Field>
        <Field label="Kuruluş Yılı"><Input value={form.founded} onChange={(e) => set("founded", e.target.value)} /></Field>
      </Section>

      <Section title="Hero (Ana Sayfa Başlık)">
        <Field label="Üst Etiket"><Input data-testid="site-hero-eyebrow" value={form.hero_eyebrow} onChange={(e) => set("hero_eyebrow", e.target.value)} /></Field>
        <Field label="Başlık (üst satır)"><Input data-testid="site-hero-top" value={form.hero_title_top} onChange={(e) => set("hero_title_top", e.target.value)} /></Field>
        <Field label="Başlık (alt satır)"><Input data-testid="site-hero-bottom" value={form.hero_title_bottom} onChange={(e) => set("hero_title_bottom", e.target.value)} /></Field>
        <Field label="Alt Metin" full><Textarea value={form.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} rows={2} /></Field>
      </Section>

      <Section title="Haftanın Önerisi">
        <Field label="Başlık" full><Input data-testid="site-weekly-title" value={form.wk_title} onChange={(e) => set("wk_title", e.target.value)} /></Field>
        <Field label="Metin" full><Textarea value={form.wk_text} onChange={(e) => set("wk_text", e.target.value)} rows={3} /></Field>
      </Section>

      <div className="border border-charcoal/10 bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-xl text-charcoal">Çalışma Saatleri</h3>
          <Button variant="outline" size="sm" onClick={addHour} data-testid="site-add-hour"><Plus className="mr-1 h-3.5 w-3.5" /> Satır Ekle</Button>
        </div>
        <div className="space-y-2">
          {form.working_hours.map((h, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input className="flex-1" placeholder="Gün (örn. Pazartesi - Cumartesi)" value={h.day} onChange={(e) => setHour(i, "day", e.target.value)} />
              <Input className="w-40" placeholder="08:00 - 20:00" value={h.hours} onChange={(e) => setHour(i, "hours", e.target.value)} />
              <button onClick={() => rmHour(i)} className="rounded p-2 text-charcoal/50 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="site-save-btn" onClick={save} disabled={saving} className="bg-bordo text-cream hover:bg-bordo-dark">
          <Save className="mr-2 h-4 w-4" /> {saving ? "Kaydediliyor…" : "Ayarları Kaydet"}
        </Button>
      </div>
    </div>
  );
}

export default function SiteSettingsForm() {
  const { data: site, isLoading } = useQuery({ queryKey: ["admin", "site"], queryFn: adminGetSite, retry: false });
  if (isLoading || !site) return <p className="py-10 text-center font-mono text-sm text-charcoal/40">Yükleniyor…</p>;
  return <SiteForm key={JSON.stringify(site)} site={site} />;
}
